import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { MOMENTUM_ACTIVO } from './config/stripeConfig';
import ErrorBoundary from './lib/ErrorBoundary';
import { AuthProvider, useAuth } from './lib/AuthProvider';
import { RequireAuth, RequireOnboarding, RequireMembership } from './lib/ProtectedRoute';
import Landing from './pages/Landing';
import About from './pages/About';
import Membresias from './pages/Membresias';
import Privacidad from './pages/Privacidad';
import Terminos from './pages/Terminos';
import EscribirHistoria from './pages/EscribirHistoria';
import Onboarding from './pages/Onboarding';
import LaSala from './pages/LaSala';
import SalaPage from './pages/SalaPage';
import MisIdeasPage from './pages/MisIdeasPage';
import MiEspacioPage from './pages/MiEspacioPage';
import EspacioOtroPage from './pages/EspacioOtroPage';
// import EscritorioSpark from './pages/EscritorioSpark'; // Disconnected — redirects to /arbol
import EscritorioMomentum from './pages/EscritorioMomentum';
import HumanStories from './pages/HumanStories';
import Perfil from './pages/Perfil';
import Dashboard from './pages/Dashboard';
import EscritorioOnsite from './pages/EscritorioOnsite';
// import EscritorioBrujula from './pages/EscritorioBrujula'; // Archived — replaced by Tu Radar
import TuRadarLanding from './pages/TuRadarLanding';
import OnsiteGrowthLanding from './pages/OnsiteGrowthLanding';
import QueEsPKM from './pages/QueEsPKM';
import PreguntasFrecuentes from './pages/PreguntasFrecuentes';
import EscritorioTuRadar from './pages/EscritorioTuRadar';
import CrearPensamiento from './pages/CrearPensamiento';
import MatchesPage from './pages/MatchesPage';
import ConexionesPage from './pages/ConexionesPage';
import ChatPage from './pages/ChatPage';
import NewsletterPage from './pages/NewsletterPage';
import ProyectosPage from './pages/ProyectosPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminRadarVideos from './components/radar/AdminRadarVideos';
import Proyecto from './pages/Proyecto';
import CasoPage from './pages/CasoPage';
import ArbolPage from './pages/ArbolPage';
import PagoExitoso from './pages/PagoExitoso';
import InstallBanner from './lib/InstallBanner';
import AdminViewSwitcher from './lib/AdminViewSwitcher';
import { PreviewProvider } from './lib/PreviewBanner';
import { ImmersiveGlobalStyles, AuroraBackground } from './lib/ImmersiveStyles';
import MobileTabBar from './components/MobileTabBar';
import { useScreen } from './lib/useScreen';

const PUBLIC_ROUTES = ['/', '/nosotros', '/membresias', '/privacidad', '/terminos', '/tu-radar', '/onsite-growth', '/que-es-pkm', '/preguntas-frecuentes'];

function MobileTabBarGlobal() {
  const { w } = useScreen();
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (w >= 900) return null;
  if (!user) return null;
  if (PUBLIC_ROUTES.includes(pathname) || pathname.startsWith('/join/') || pathname.startsWith('/caso/')) return null;
  return <MobileTabBar />;
}

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <PreviewProvider>
      <Router>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/nosotros" element={<About />} />
          <Route path="/membresias" element={<Membresias />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/terminos" element={<Terminos />} />
          <Route path="/join/spark-seeker" element={<Landing />} />
          <Route path="/join/momentum" element={MOMENTUM_ACTIVO ? <Landing /> : <Navigate to="/membresias" replace />} />
          <Route path="/join/growth" element={<Landing />} />
          <Route path="/tu-radar" element={<TuRadarLanding />} />
          <Route path="/onsite-growth" element={<OnsiteGrowthLanding />} />
          <Route path="/que-es-pkm" element={<QueEsPKM />} />
          <Route path="/preguntas-frecuentes" element={<PreguntasFrecuentes />} />
          <Route path="/caso/:slug" element={<CasoPage />} />

          {/* OAuth redirect hub */}
          <Route path="/dashboard" element={<Dashboard />} />

          {/* Auth required, onboarding NOT required */}
          <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />

          {/* Auth + onboarding required */}
          <Route path="/sala" element={<RequireOnboarding><SalaPage /></RequireOnboarding>} />
          <Route path="/sala/mi-espacio" element={<RequireOnboarding><MiEspacioPage /></RequireOnboarding>} />
          <Route path="/sala/espacio/:userId" element={<RequireOnboarding><EspacioOtroPage /></RequireOnboarding>} />
          <Route path="/la-sala" element={<RequireOnboarding><SalaPage /></RequireOnboarding>} />
          <Route path="/mis-ideas" element={<RequireOnboarding><MisIdeasPage /></RequireOnboarding>} />
          <Route path="/stories" element={<RequireOnboarding><HumanStories /></RequireOnboarding>} />
          <Route path="/stories/escribir" element={<RequireOnboarding><EscribirHistoria /></RequireOnboarding>} />
          <Route path="/perfil" element={<RequireOnboarding><Perfil /></RequireOnboarding>} />
          <Route path="/matches" element={<RequireOnboarding><MatchesPage /></RequireOnboarding>} />
          <Route path="/conexiones" element={<RequireOnboarding><ConexionesPage /></RequireOnboarding>} />
          <Route path="/chat/:conexionId" element={<RequireOnboarding><ChatPage /></RequireOnboarding>} />
          <Route path="/newsletter" element={<RequireOnboarding><NewsletterPage /></RequireOnboarding>} />
          <Route path="/proyectos" element={<RequireOnboarding><ProyectosPage /></RequireOnboarding>} />
          <Route path="/proyecto/:userId" element={<RequireOnboarding><Proyecto /></RequireOnboarding>} />
          <Route path="/arbol" element={<RequireAuth><ArbolPage /></RequireAuth>} />
          <Route path="/pago-exitoso" element={<RequireAuth><PagoExitoso /></RequireAuth>} />
          <Route path="/pensamientos/nuevo" element={<RequireAuth><CrearPensamiento /></RequireAuth>} />

          {/* Auth + onboarding + membership required */}
          <Route path="/escritorio/spark" element={<Navigate to="/arbol" replace />} />
          <Route path="/escritorio/momentum" element={MOMENTUM_ACTIVO ? <RequireMembership minimum="momentum_builder"><EscritorioMomentum /></RequireMembership> : <Navigate to="/membresias" replace />} />
          <Route path="/escritorio/onsite" element={<RequireMembership minimum="onsite_mexico"><EscritorioOnsite /></RequireMembership>} />
          {/* /escritorio/brujula archived — replaced by /escritorio/tu-radar */}
          <Route path="/escritorio/tu-radar" element={<RequireMembership minimum="tu_radar"><EscritorioTuRadar /></RequireMembership>} />
          <Route path="/admin" element={<RequireAuth><AdminDashboard /></RequireAuth>} />
          <Route path="/admin/entregables" element={<RequireAuth><AdminDashboard /></RequireAuth>} />
          <Route path="/admin/radar-videos" element={<RequireAuth><div style={{ minHeight:'100vh', background:'#0a0a0a', padding:'2rem', fontFamily:"'DM Sans', sans-serif" }}><div style={{ maxWidth:800, margin:'0 auto' }}><AdminRadarVideos /></div></div></RequireAuth>} />
        </Routes>
        <MobileTabBarGlobal />
      </Router>
      </PreviewProvider>
      <ImmersiveGlobalStyles />
      <AuroraBackground />
      <InstallBanner />
    </AuthProvider>
    </ErrorBoundary>
  );
}
